import {
  ownVisualAssignment,
  registerVisualRoot,
  snapshotOwnedVisualData,
} from '@/utils/OwnedBundleVisualData';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { SIInputMode } from '@/models/ChipModel';
import { makeAutoObservable, observable, toJS } from 'mobx';
import { z } from 'zod';
import { GlobalTilePosition } from '@/types';
import { createTileId, TileId } from '@/types/ids';
import { deepClone } from '@/utils/clone';
import { Rotation } from '@/models/data/Rotation';
import { TileType } from '@/models/TileTypes';
import {
  ContextMenuActionFn,
  createAllLowSignal,
  TileMeta,
  TileSideIO,
} from '@/models/tiles/TileDefinition';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import { Side, TileInstanceDto, TileInstanceDtoSchema } from '@/schemas/tiles';
import Context from '@/context';
import { isFixedTile, isNamedChallengeInterface } from '@/challenges/editing';
import { runtimeGraphIds, type RuntimeNodeImport } from '@/models/logic/RuntimeGraphIds';
import { observableTileMeta, ownTileMetaAssignment } from '@/models/tiles/SignalSides';

export const setupDefaultMeta = (): TileMeta => ({
  inputs: createAllLowSignal(),
  outputs: createAllLowSignal(),
  rotation: Rotation.CW_0,
  partOfChip: null,
});

// Store context refs outside MobX's reach. MobX deep-converts plain objects
// assigned to class properties, which snapshots context at construction time
// and misses later mutations (e.g. context.editor set after chip loading).
const contextMap = new WeakMap<TileInstance, Context>();

/** Native identity check; opaque proxies and objects imitating tiles are excluded. */
export function isConstructedTileInstance(value: unknown): value is TileInstance {
  return (
    typeof value === 'object' && value !== null && contextMap.has(value as TileInstance)
  );
}

export class TileInstance<TState = unknown> {
  private importedNodeIds?: Map<NodeHandle, string>;
  id: TileId = createTileId();
  meta: TileMeta;
  state: TState;
  actions: Record<string, () => void> = {};
  logicNodeIds?: Partial<Record<Side, NodeHandle>>;
  effectiveIoSides?: Record<Side, TileSideIO>;
  laneNodeIds?: Partial<Record<Side, NodeHandle[]>>;
  cablePort = false;
  adapterPort = false;
  adapterPin = false;
  adapterSignalIds?: string[];
  adapterSignalLabels?: Record<string, string>;
  adapterSignalAliases?: Record<string, string[]>;
  laneSignalIds?: Partial<Record<Side, string[][]>>;
  laneSignalLabels?: Partial<Record<Side, string[]>>;
  bundleSignalLabels?: Record<string, string>;
  laneVisualFlips?: Partial<Record<Side, boolean>>;
  laneInputs?: Partial<Record<Side, import('@/models/data/Signal').Signal[]>>;
  laneOutputs?: Partial<Record<Side, import('@/models/data/Signal').Signal[]>>;
  bundleErrors: string[] = [];
  renderVersion = 0;
  /** Set on preview tiles to indicate they can't be placed (e.g. chip doesn't fully fit) */
  previewBlocked = false;
  /** Gesture-only label artwork; never registered as a real interface or serialized. */
  previewInterfaceLink?: import('@/models/ChipModel').InterfaceLink;

  constructor(
    context: Context,
    public tileType: TileType,
    public globalTilePosition: GlobalTilePosition,
    options: { simulationOnly?: boolean } = {},
  ) {
    contextMap.set(this, context);
    this.meta = setupDefaultMeta();

    const tileDefinition = tileDefinitionRegistry.get(tileType);
    this.state = tileDefinition.createDefaultState({
      meta: this.meta,
      globalTilePosition,
      eventBus: context.eventBus,
    }) as TState;
    // Template copies are private graph-build data: no UI observers or actions.
    if (options.simulationOnly) return;
    this.actions =
      tileDefinition.createActions?.({ instance: this, eventBus: context.eventBus }) ??
      {};
    // Third-party definitions retain the complete ordinary MobX property API.
    const normalizeSignals = !tileType.startsWith('extension:');
    if (normalizeSignals) this.meta = observableTileMeta(this.meta);

    makeAutoObservable<this, 'importedNodeIds'>(this, {
      id: false,
      globalTilePosition: observable.ref,
      laneSignalIds: observable.ref,
      laneSignalLabels: observable.ref,
      bundleSignalLabels: observable.ref,
      context: false,
      contextMenuItems: false,
      importedNodeIds: false,
    });
    if (normalizeSignals) registerVisualRoot(this);
  }

  get context(): Context {
    return contextMap.get(this)!;
  }

  get location() {
    return this.globalTilePosition.location;
  }

  static fromDto(
    dto: TileInstanceDto,
    context: Context,
    importer: RuntimeNodeImport = runtimeGraphIds(context).importer(),
  ) {
    TileInstanceDtoSchema.parse(dto);
    const instance = new TileInstance(context, dto.tileType, dto.globalTileLocation);
    const ownedMeta = deepClone(dto.meta);
    instance.meta = ownTileMetaAssignment(instance, ownedMeta);
    // The prototype Cable was always a Wire. Preserve all geometry/ports while
    // normalizing its obsolete placement marker to the ordinary Wire model.
    if (dto.tileType === TileType.Wire) delete instance.meta.cable;
    const stateSchema = tileDefinitionRegistry.get(dto.tileType).stateSchema;
    instance.state = deepClone(
      stateSchema ? stateSchema.parse(dto.state) : dto.state,
    ) as typeof instance.state;
    if (
      instance.tileType === TileType.Button ||
      (instance.tileType === TileType.SingleInterface &&
        (instance.state as { inputMode?: SIInputMode })?.inputMode === SIInputMode.BUTTON)
    ) {
      (instance.state as { toggleState: number }).toggleState = 0;
    }
    instance.id = dto.id as TileId;
    const importId = (value: string) => {
      const id = importer.node(value);
      (instance.importedNodeIds ??= new Map()).set(id, value);
      return id;
    };
    instance.logicNodeIds = dto.logicNodeIds
      ? Object.fromEntries(
          Object.entries(dto.logicNodeIds).map(([side, id]) => [side, importId(id!)]),
        )
      : undefined;
    for (const key of instance.runtimeStateIdFields()) {
      const state = instance.state as Record<string, unknown>;
      if (state && typeof state[key] === 'string') state[key] = importId(state[key]);
    }
    return instance;
  }

  get dto(): TileInstanceDto {
    // Sanitize logicNodeIds: only include valid Side keys with non-null values
    // to prevent Zod validation failures from stale entries
    let cleanLogicNodeIds: Partial<Record<Side, string>> | undefined;
    if (this.logicNodeIds) {
      const sides = [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT] as const;
      for (const side of sides) {
        const nodeId = this.logicNodeIds[side];
        if (nodeId != null) {
          if (!cleanLogicNodeIds) cleanLogicNodeIds = {};
          cleanLogicNodeIds[side] = this.nodeIdText(nodeId);
        }
      }
    }
    return {
      id: this.id,
      meta: (() => {
        const meta = snapshotOwnedVisualData(this.meta);
        if (this.tileType === TileType.Wire) delete meta.cable;
        return meta;
      })(),
      state: this.stateDto(),
      tileType: this.tileType,
      // Position inputs may already contain MobX proxies. Capture schema fields
      // explicitly, including coordinates supplied by nonenumerable getters.
      globalTileLocation: {
        chipInstanceId: this.globalTilePosition.chipInstanceId,
        circuitType: this.globalTilePosition.circuitType,
        location: {
          x: this.globalTilePosition.location.x,
          y: this.globalTilePosition.location.y,
        },
      },
      logicNodeIds: cleanLogicNodeIds,
    };
  }

  private nodeIdText(id: NodeHandle): string {
    return this.importedNodeIds?.get(id) ?? runtimeGraphIds(this.context).nodeText(id);
  }

  /** Unpublished exact hydration only: keep all numeric and boundary bindings coherent. */
  remapContinuationBindings(
    remap: ReadonlyMap<NodeHandle, NodeHandle>,
    graph: import('@/interfaces/models').ILogicGraph,
  ): void {
    if (!this.context.isHydrating) throw Error('Continuation binding outside hydration');
    const id = (previous: NodeHandle) => {
      const next = remap.get(previous) ?? previous;
      if (graph.nodes.has(next))
        (this.importedNodeIds ??= new Map()).set(next, graph.nodeIdText(next));
      return next;
    };
    if (this.logicNodeIds)
      this.logicNodeIds = Object.fromEntries(
        Object.entries(this.logicNodeIds).map(([side, previous]) => [
          side,
          previous === undefined ? undefined : id(previous),
        ]),
      );
    if (this.laneNodeIds)
      this.laneNodeIds = Object.fromEntries(
        Object.entries(this.laneNodeIds).map(([side, values]) => [side, values.map(id)]),
      );
    if (this.state && typeof this.state === 'object') {
      for (const key of this.runtimeStateIdFields()) {
        const state = this.state as Record<string, unknown>;
        if (typeof state[key] === 'number') state[key] = id(state[key] as NodeHandle);
      }
    }
  }

  private runtimeStateIdFields(): readonly string[] {
    if (this.tileType === TileType.And) return ['topNodeId', 'bottomNodeId', 'outNodeId'];
    if (this.tileType === TileType.Bridge) return ['verticalNodeId', 'horizontalNodeId'];
    return [];
  }

  private stateDto(): unknown {
    const state = toJS(this.state);
    const fields = this.runtimeStateIdFields();
    if (!fields.length || !state || typeof state !== 'object') return state;
    const result = { ...state } as Record<string, unknown>;
    for (const key of fields)
      if (typeof result[key] === 'number')
        result[key] = this.nodeIdText(result[key] as NodeHandle);
    return result;
  }

  get contextMenuItems(): Array<{ label: string; action: ContextMenuActionFn }> {
    if (isFixedTile(this) || isNamedChallengeInterface(this)) return [];
    const chipEditorModel = this.context.editor?.currentChipEditor;
    if (!chipEditorModel) return [];
    const tileDefinition = tileDefinitionRegistry.get(this.tileType);
    return (
      tileDefinition.createContextMenuItems?.({
        instance: this,
        chipEditorModel,
      }) ?? []
    );
  }

  clone(context: Context) {
    const tile: TileInstance = new TileInstance(
      context,
      this.tileType,
      this.globalTilePosition,
    );

    tile.meta = deepClone(this.meta);
    tile.state = deepClone(this.state);
    if (this.importedNodeIds) {
      runtimeGraphIds(context).assertSame(runtimeGraphIds(this.context));
      tile.importedNodeIds = new Map(this.importedNodeIds);
    }
    tile.effectiveIoSides = this.effectiveIoSides;

    return tile;
  }

  cloneMeta(): TileMeta {
    return {
      ...deepClone(this.meta),
      inputs: { ...this.meta.inputs },
      outputs: { ...this.meta.outputs },
      rotation: this.meta.rotation,
      partOfChip: this.meta.partOfChip,
    };
  }
}
